import React, { useState, useRef, useMemo } from "react";
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { useLeaveEncashment } from "../../../hooks/useLeaveEncashment";
import { useFilterManager } from "../../../hooks/useFilterManager";
import { DynamicFilterBar } from "../../filters/DynamicFilterBar";
import { Button, SearchBar } from "../../../../common/APICommon";
import { LoadingSpinner } from "../../../../common/Loading";
import { Select } from "../../../../common/Select";
import { ConfirmationModal } from "../../../../common/Modal"; 
import { buildMasterlistWorksheet } from "../../../../utils/employee/buildWorksheet";
import { FEATURES } from "../../../../config/features";
import LeaveEncashmentTable from "./components/LeaveEncashmentTable";
import LeaveEncashmentBreakdown from "./components/LeaveEncashmentBreakdown";
import LeaveEncashmentSyncSummary from "./components/LeaveEncashmentSyncSummary";

const LeaveEncashmentModule = ({ user, showGeneralMessage }) => {
  const [showSettings, setShowSettings] = useState(false);
  const settingRef = useRef(null);

  // Modal & Selection States
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [targetPayrollPeriodId, setTargetPayrollPeriodId] = useState(null);
  const [targetProcess, setTargetProcess] = useState("payroll"); // ADDED: track destination
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isFinalConfirmOpen, setIsFinalConfirmOpen] = useState(false); 

  // 1. Hook
  const { activeFilterState, onFilterChange, apiFilterPayload, applyFilters, resetFilters } = useFilterManager(FEATURES.EMPLOYEE_FILTERS || []);

  // 2. Hook
  const {
    loading, data, setData, handleSearch,
    selectedYear, setSelectedYear,
    postToPayroll,
    refresh
  } = useLeaveEncashment({ showGeneralMessage, apiFilterPayload });

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [
      { value: currentYear - 1, label: (currentYear - 1).toString() },
      { value: currentYear, label: currentYear.toString() },
      { value: currentYear + 1, label: (currentYear + 1).toString() },
    ];
  }, []);

  const syncSummaryData = useMemo(() => {
    return data.filter(emp => selectedIds.includes(emp.id) && parseFloat(emp.totalEncashable) > 0);
  }, [data, selectedIds]);

  const totalAmountToPost = useMemo(() => {
    return syncSummaryData.reduce((sum, item) => sum + parseFloat(item.totalGross || 0), 0);
  }, [syncSummaryData]);

  const handleOpenSyncModal = () => {
    if (selectedIds.length === 0) {
      showGeneralMessage("Please select at least one employee from the table.", false);
      return;
    }
    if (syncSummaryData.length === 0) {
      showGeneralMessage("None of the selected employees have encashable balances.", false);
      return;
    }
    setIsSyncModalOpen(true);
  };

  // MODIFIED: Receives both Period and Process from Step 1 Modal
  const handleProceedToFinalConfirm = (periodId, process) => {
    setTargetPayrollPeriodId(periodId);
    setTargetProcess(process); // Set whether this is Payroll or Final Pay
    setIsSyncModalOpen(false); 
    setIsFinalConfirmOpen(true); 
  };

  const handleFinalExecuteSync = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    // Pass the targetProcess state to the hook
    const success = await postToPayroll(selectedIds, targetPayrollPeriodId, targetProcess);

    if (success) {
      setSelectedIds([]);
      setTargetPayrollPeriodId(null);
      setIsFinalConfirmOpen(false);
      refresh();
    }
  };

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const section = {
      label: "Leave Inquiry Summary",
      fields: [
        { key: "employeeId", label: "Emp ID", width: 12 },
        { key: "fullName", label: "Name", width: 30 },
        { key: "totalLeaveBalance", label: "Total Bal", width: 12 },
        { key: "totalEncashable", label: "Encashable", width: 12 },
        { key: "totalGross", label: "Total Value", width: 15 },
      ]
    };
    buildMasterlistWorksheet(workbook, "Summary", section, data);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Leave_Encashment_Summary.xlsx`);
    setShowSettings(false);
  };

  return (
    <div className="relative">
      <LoadingSpinner loading={loading} />

      <div className="w-full py-2 px-4 box-border">
        <div className="text-sm">
          <span className="text-gray-400">{"Transaction > "}</span>
          <span className="border-b border-black text-gray-700">{"Leave Encashment"}</span>
        </div>
        <div className="flex items-center justify-between border-b bg-white">
          <h3 className="text-xl font-bold m-0 text-customMainColor tracking-tight uppercase">
            Leave Encashment
          </h3>
          <div className="flex items-center gap-3">
            <div className="w-32 hidden">
              <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} options={yearOptions} />
            </div>
            <div className="relative flex items-center" ref={settingRef}>
              <button onClick={() => setShowSettings(!showSettings)} className="flex items-center gap-1 bg-blue-800 p-2 rounded hover:bg-blue-900 border-none cursor-pointer shadow-sm transition-all">
                <SettingsIcon className="text-white" fontSize="small" />
                <KeyboardArrowDownIcon className="text-white" fontSize="small" />
              </button>
              {showSettings && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white shadow-2xl border border-gray-200 rounded-lg z-50 overflow-hidden animate-in fade-in zoom-in duration-100">
                  <button onClick={handleExport} className="w-full text-left px-4 py-3 hover:bg-blue-50 flex items-center gap-2 border-none bg-transparent cursor-pointer text-gray-700 transition-colors">
                    <FileDownloadIcon fontSize="small" className="text-blue-600" />
                    <span className="text-sm font-medium">Export Summary to Excel</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" style={{ border: "1px solid #d7d7d7" }}>
          <div className="border-b border-gray-100 mb-4">
            <span className="text-lg font-semibold text-gray-600">Filters</span>
          </div>
          <DynamicFilterBar requestedFilters={FEATURES.EMPLOYEE_FILTERS || []} activeFilterState={activeFilterState} onFilterChange={onFilterChange} showButtons={false} />
          <div className="flex gap-2 mt-5 border-t border-gray-200 pt-4">
            <Button name="GENERATE" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 border-none shadow cursor-pointer font-bold rounded-lg" onClick={applyFilters} />
            <Button name="POST/SYNC DATA" icon={<SyncAltIcon />} className="bg-green-600 hover:bg-green-700 text-white px-5 py-3 border-none shadow cursor-pointer font-bold rounded-lg" onClick={handleOpenSyncModal} />
            <Button name="CLEAR" className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-3 border-none shadow cursor-pointer rounded-lg" onClick={() => { resetFilters(); setData([]); }} />
          </div>
        </div>

        <SearchBar handleFilter={handleSearch} />

        <LeaveEncashmentTable data={data} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onViewDetail={(emp) => setSelectedEmployee(emp)} />

        <LeaveEncashmentBreakdown isOpen={!!selectedEmployee} onClose={() => setSelectedEmployee(null)} employee={selectedEmployee} />

        {/* STEP 1 Modal */}
        <LeaveEncashmentSyncSummary
          isOpen={isSyncModalOpen}
          onClose={() => setIsSyncModalOpen(false)}
          onConfirm={handleProceedToFinalConfirm} 
          selectedData={syncSummaryData}
          selectedYear={selectedYear}
          loading={loading}
        />

        {/* STEP 2 Modal */}
        <ConfirmationModal
          visible={isFinalConfirmOpen}
          onClose={() => setIsFinalConfirmOpen(false)}
          onConfirm={handleFinalExecuteSync}
          title={`Confirm Posting to ${targetProcess === 'payroll' ? 'Regular Payroll' : 'Final Pay'}`}
          message={`Are you sure you want to proceed with posting encashments for ${syncSummaryData.length} employees?`}
          noteMessage={`Total amount to be credited: ₱${totalAmountToPost.toLocaleString()}. This will create entries in the ${targetProcess} staging table.`}
          confirmText="Yes, Proceed"
          type="info"
          isLoading={loading}
        />
      </div>
    </div>
  );
};

export default LeaveEncashmentModule;